import { customElement, property } from "lit/decorators.js";
import { html, TemplateResult, PropertyValues } from "lit";
import { MachineAnnotation, machineAnnotationState } from "@/state/MachineAnnotations";
import { UserAnnotation, userAnnotationState } from "@/state/UserAnnotations";
import { initTooltips } from "@/utilities";
import { annotationState, compareAnnotationOrders } from "@/state/Annotations";
import { DodonaElement } from "@/components/meta/dodona_element";
import { I18n } from "@/i18n/i18n";

/**
 * This component represents a dot that shows the number of hidden annotations for a line.
 *
 * @element d-hidden-annotations-dot
 *
 * @prop {number} row - The row number.
 */
@customElement("d-hidden-annotations-dot")
export class HiddenAnnotationsDot extends DodonaElement {
    @property({ type: Number })
    row: number;

    get machineAnnotations(): MachineAnnotation[] {
        return machineAnnotationState.byLine.get(this.row) || [];
    }

    get userAnnotations(): UserAnnotation[] {
        return userAnnotationState.rootIdsByLine.get(this.row)?.map((id) => userAnnotationState.byId.get(id)) || [];
    }

    get hiddenAnnotations(): (MachineAnnotation | UserAnnotation)[] {
        return [...this.machineAnnotations, ...this.userAnnotations].filter((a) => !annotationState.isVisible(a));
    }

    private get hiddenSummary(): { count: number; type?: string; title: string; classes: string } {
        const hidden = this.hiddenAnnotations;
        const count = hidden.length;

        const title =
            count === 1 ? I18n.t("js.annotation.hidden.single") : I18n.t("js.annotation.hidden.plural", { count });

        // Pick the most important hidden annotation type for styling
        const type = hidden.slice().sort(compareAnnotationOrders)[0]?.type;
        const classes = type ? `dot-${type}` : "";

        return { count, type, title, classes };
    }

    protected updated(): void {
        initTooltips(this);
    }

    render(): TemplateResult {
        const { count, title, classes } = this.hiddenSummary;
        if (count === 0) return html``;

        // Make it reachable for keyboard users and expose a readable name.
        // Tooltip: enable focus trigger so keyboard users can discover it.
        return html`
            <span
                class="dot ${classes}"
                role="img"
                tabindex="0"
                aria-label=${title}
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                data-bs-trigger="hover focus"
                title=${title}
            ></span>
        `;
    }
}
